<template>
  <div class="q-pa-md">
    <q-table
      title="Users"
      :rows="users"
      :columns="columns"
      row-key="userId"
      :visible-columns="visibleColumns"
      v-model:pagination="pagination"
      :rows-per-page-options="[5, 10, 25, 50]"
      @request="onRequest"
      binary-state-sort
      no-data-label="No users found within given search criteria"
    >
      <template v-slot:top-right>
        <q-input
          v-model.trim="searchModel"
          placeholder="Search"
          class="q-mr-xs"
          @keydown.enter="onRequest"
          dense
        >
          <template v-slot:append>
            <q-icon name="search" />
          </template>
        </q-input>
        <q-toggle
          v-model="isShowingRaOnly"
          class="q-mr-md"
          color="primary"
          label="RAs Only"
          no-caps
          @click="onRequest(props)"
        />
        <q-select
          class="q-mr-xs"
          v-model="visibleColumns"
          multiple
          outlined
          dense
          options-dense
          display-value="Filter"
          emit-value
          map-options
          :options="columns"
          option-value="name"
          options-cover
          style="min-width: 150px"
        >
          <template #option="scope">
            <q-item
              clickable
              @click="scope.toggleOption(scope.opt)"
            >
              <q-item-section side>
                <q-checkbox
                  :model-value="scope.selected"
                  @click="scope.toggleOption(scope.opt)"
                />
              </q-item-section>

              <q-item-section>
                {{ scope.opt.name }}
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-btn
          class="q-mr-xs"
          color="primary"
          icon-right="sync"
          no-caps
          @click="onVerifyVaccinations()"
        >
          <q-tooltip class="text-body2">
            Verify the vaccinations for all the students.
          </q-tooltip>
        </q-btn>
      </template>
      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
          >
            {{ col.label }}
          </q-th>
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr :props="props">
          <q-td
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
          >
            <div v-if="col.field === 'username' && hasRoles('R&D', 'IT')">
              <q-popup-edit
                v-model="props.row.username"
                buttons
                v-slot="scope"
                @save="(val) => {submitInputFromColumn(val, props.row, col.name)}"
              >
                <q-input
                  v-model="scope.value"
                  @keyup.enter="scope.set"
                />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>
            <div v-else-if="col.field === 'isRA'">
              <q-checkbox
                v-model="props.row.isRA"
                :label="$filters.yesNo(props.row.isRA)"
                size="xs"
                color="green"
                @update:model-value="callUpdateUserRaStatus(props.row)"
              />
            </div>
            <div v-else>
              {{ col.value }}
            </div>
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </div>
</template>

<script>
import axios from 'axios';
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import useAuth from '@/hooks/auth';
import useUser from '@/hooks/user';
import useUiUtilities from '@/utilities/uiUtilities';

export default {
  async setup() {
    const $q = useQuasar();

    const { hasRoles } = useAuth();
    const { updateUser, updateUserRaStatus, verifyVaccinations } = useUser();
    const { notifyResult } = useUiUtilities();

    const isShowingRaOnly = ref(false);
    const searchModel = ref('');
    const users = ref();

    const pagination = ref({
      rowsPerPage: 10,
      rowsNumber: -1,
      page: 1,
      sortBy: '',
      descending: false,
    });
    const columns = [
      {
        name: 'First Name', align: 'left', label: 'First Name', field: 'firstName', sortable: true,
      },
      {
        name: 'Last Name', align: 'left', label: 'Last Name', field: 'lastName', sortable: true,
      },
      {
        name: 'Preferred Name', align: 'left', label: 'Preferred Name', field: 'preferredFirstName', sortable: true,
      },
      {
        name: 'User Name', align: 'left', label: 'User Name', field: 'username', sortable: true,
      },
      {
        name: 'Colleague ID', align: 'left', label: 'Colleague ID', field: 'colleagueId', sortable: true,
      },
      {
        name: 'Vaccine Status', align: 'left', label: 'Vaccine Status', field: 'vaccinationStatus', sortable: true,
      },
      {
        name: 'Is RA', align: 'left', label: 'Is RA', field: 'isRA',
      },
    ];

    const fetchUsers = async (
      page = 1, rowsPerPage = 10, descending, sortBy, isRA = false,
    ) => {
      try {
        const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Users`;
        const response = await axios.get(url, {
          params: {
            page,
            rowsPerPage,
            descending,
            sortBy,
            filter: searchModel.value,
            isRA,
          },
        });
        const { data } = response;
        users.value = data.data;
        pagination.value.rowsNumber = data.rowsNumber;
      } catch (error) {
        console.log(error);
      }
    };
    await fetchUsers(pagination.value.page, pagination.value.rowsPerPage);

    const onRequest = async (props) => {
      $q.loading.show();
      const page = props?.pagination?.page ?? 1;
      const rowsPerPage = props?.pagination?.rowsPerPage ?? pagination.value.rowsPerPage;
      const sortBy = props?.pagination?.sortBy ?? pagination.value.sortBy;
      const descending = props?.pagination?.descending ?? pagination.value.descending;
      await fetchUsers(
        page, rowsPerPage, descending, sortBy, isShowingRaOnly.value,
      );

      pagination.value.page = page;
      pagination.value.rowsPerPage = rowsPerPage;
      pagination.value.sortBy = sortBy;
      pagination.value.descending = descending;

      $q.loading.hide();
    };

    const callUpdateUser = async (row) => {
      const response = await updateUser(row);
      if (response.status !== 200 && response.status !== 201) {
        notifyResult(response.status, response);
      }
    };

    const callUpdateUserRaStatus = async (row) => {
      const response = await updateUserRaStatus(row);
      if (response.status !== 200 && response.status !== 201) {
        notifyResult(response.status, response);
      }
    };

    const submitInputFromColumn = async (val, userModel, columnName) => {
      if (columnName === 'User Name') {
        userModel.username = val; // eslint-disable-line no-param-reassign
      } else {
        console.log('column type not handled for update');
      }
      await callUpdateUser(userModel);
    };

    const onVerifyVaccinations = async () => {
      $q.loading.show();
      const result = await verifyVaccinations();
      $q.loading.hide();

      const sync = result?.succeeded ? result.data : null;
      if (sync) {
        if (sync.fetched === 0) {
          $q.notify({
            message: 'MedProctor responded but returned 0 vaccination records, so nothing was updated.',
            color: 'warning',
            textColor: 'white',
            position: 'top',
            timeout: 0,
            actions: [{ label: 'Dismiss', color: 'white' }],
          });
        } else {
          const skippedNote = sync.skippedMissingStudentId > 0
            ? `, ${sync.skippedMissingStudentId} records skipped (missing student id)`
            : '';
          const preservedNote = sync.preservedExisting > 0
            ? `, ${sync.preservedExisting} existing statuses preserved against unreliable feed values`
            : '';
          notifyResult(
            200, null, false,
            `MedProctor sync complete: ${sync.fetched} records fetched, `
              + `${sync.matched} students matched, ${sync.rowsWritten} rows updated${skippedNote}${preservedNote}.`,
          );
        }
        await onRequest();
      } else {
        notifyResult(result?.response?.status ?? 500, result?.response, true);
      }
    };

    return {
      callUpdateUser,
      callUpdateUserRaStatus,
      columns,
      hasRoles,
      isEditModalActive: ref(false),
      isShowingRaOnly,
      onRequest,
      pagination,
      searchModel,
      submitInputFromColumn,
      onVerifyVaccinations,
      users,
      visibleColumns: ref([
        'First Name',
        'Last Name',
        'Preferred Name',
        'Vaccine Status',
        'Is RA',
        'User Name',
      ]),
    };
  },
};
</script>
